<template>
  <div class="leave-encashment-slip">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Leave Encashment Slip</h4>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Slip</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div class="slip-page">
      <div class="slip-border">
        <!-- Header -->
        <div class="slip-header">
          <img
            v-if="schoolLogo"
            :src="schoolLogo"
            alt="Logo"
            class="slip-logo"
          />
          <div class="slip-header-text">
            <h1 class="school-name">{{ displayCampusName }}</h1>
            <p v-if="campusAddress" class="school-address">{{ campusAddress }}</p>
            <h2 class="slip-title">Leave Encashment Slip</h2>
          </div>
        </div>

        <div class="slip-divider"></div>

        <!-- Employee details -->
        <div class="employee-info">
          <div class="info-col">
            <div class="info-row">
              <span class="info-label">Employee ID</span>
              <span class="info-sep">:</span>
              <span class="info-value">{{ staffId }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Date</span>
              <span class="info-sep">:</span>
              <span class="info-value">{{ formattedDate }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Print Date</span>
              <span class="info-sep">:</span>
              <span class="info-value">{{ printDate }}</span>
            </div>
          </div>
          <div class="info-col">
            <div class="info-row">
              <span class="info-label">Employee Name</span>
              <span class="info-sep">:</span>
              <span class="info-value info-name">{{ displayName }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Designation</span>
              <span class="info-sep">:</span>
              <span class="info-value">{{ designation || "—" }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Department</span>
              <span class="info-sep">:</span>
              <span class="info-value">{{ department || "—" }}</span>
            </div>
          </div>
        </div>

        <div class="slip-divider"></div>

        <!-- Leave balances + amount -->
        <div class="details-grid">
          <div class="details-panel leave-panel">
            <div class="panel-head">Leave Balance</div>
            <div class="panel-body">
              <div class="balance-row">
                <span class="balance-label">Leave</span>
                <span class="balance-value">{{ displayRemainingLeaves }}</span>
              </div>
              <div class="balance-row">
                <span class="balance-label">Sick Leaves</span>
                <span class="balance-value">{{ displayRemainingSickLeaves }}</span>
              </div>
            </div>
          </div>

          <div class="details-panel amount-panel">
            <div class="panel-head">Amount</div>
            <div class="panel-body">
              <div class="balance-row amount-only-row">
                <span class="balance-label">Encashment Amount</span>
                <span class="balance-value amount-value">
                  {{ formatCurrency(encashmentAmount) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="notes" class="notes-section">
          <div class="notes-label">Description / Notes</div>
          <div class="notes-box">{{ notes }}</div>
        </div>
      </div>

      <div class="signature-row">
        <div class="signature-block">
          <div class="signature-line"></div>
          <span>Checked By</span>
        </div>
        <div class="signature-block">
          <div class="signature-line"></div>
          <span>Approved By</span>
        </div>
        <div class="signature-block">
          <div class="signature-line"></div>
          <span>Received By</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "LeaveEncashmentSlipPrint",
  data() {
    return {
      db: "",
      cID: "",
      rfID: "",
      name: "",
      designation: "",
      department: "",
      date: "",
      encashmentAmount: 0,
      remaining_leaves: null,
      remaining_sickLeaves: null,
      notes: "",
      campusName: "MY SKOOL",
      campusAddress: "",
      schoolLogo: null,
      originalPageTitle: "",
      autoPrint: false,
    };
  },
  computed: {
    displayCampusName() {
      return this.campusName || "MY SKOOL";
    },
    staffId() {
      return this.rfID || "—";
    },
    displayName() {
      return (this.name || "—").toUpperCase();
    },
    formattedDate() {
      return this.formatLongDate(this.date);
    },
    printDate() {
      return this.formatLongDate(new Date().toISOString().slice(0, 10));
    },
    displayRemainingLeaves() {
      return this.formatBalance(this.remaining_leaves);
    },
    displayRemainingSickLeaves() {
      return this.formatBalance(this.remaining_sickLeaves);
    },
  },
  created() {
    this.originalPageTitle = document.title;
    document.title = " ";
    this.initSlip();
  },
  beforeDestroy() {
    if (this.originalPageTitle) {
      document.title = this.originalPageTitle;
    }
  },
  mounted() {
    if (this.autoPrint) {
      this.$nextTick(() => {
        setTimeout(() => this.print(), 500);
      });
    }
  },
  methods: {
    initSlip() {
      const query = this.$route.query;
      this.db = query.db || this.$store.state.userData.db;
      this.cID = query.cID || query.cId || this.$store.state.userData.cId || 0;
      this.rfID = query.rfID || "";
      this.name = query.name || "";
      this.designation = query.designation || "";
      this.department = query.department || "";
      this.date = query.date || "";
      this.encashmentAmount = Number(query.encashmentAmount) || 0;
      this.remaining_leaves = this.parseBalance(query.remaining_leaves);
      this.remaining_sickLeaves = this.parseBalance(query.remaining_sickLeaves);
      this.notes = query.notes || "";
      this.autoPrint = query.autoPrint === "1" || query.autoPrint === "true";

      this.loadCampusInfo();
    },
    parseBalance(val) {
      if (val == null || val === "") return null;
      const n = Number(val);
      return Number.isNaN(n) ? val : n;
    },
    formatBalance(val) {
      if (val == null || val === "") return "—";
      return val;
    },
    formatLongDate(val) {
      if (!val) return "—";
      const d = new Date(val);
      if (Number.isNaN(d.getTime())) return String(val);
      const day = d.getDate();
      const month = d.toLocaleString("en-GB", { month: "long" });
      const year = d.getFullYear();
      return `${day}-${month}-${year}`;
    },
    formatCurrency(amount) {
      const num = Number(amount) || 0;
      return num.toLocaleString("en-PK", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    },
    async loadCampusInfo() {
      try {
        const url = `${this.$store.state.domain}campuses`;
        const response = await axios.get(url, {
          params: { db: this.db, cId: this.cID },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          if (match) {
            this.campusName = match.name;
            this.campusAddress = match.address || match.branch || "";
            this.schoolLogo = match.logo;
          }
        }
      } catch (e) {
        const currentBranch = this.$store.state.currentBranch;
        if (currentBranch) {
          this.campusName =
            currentBranch.organizationName ||
            currentBranch.branch ||
            "MY SKOOL";
          this.campusAddress =
            currentBranch.address || currentBranch.branch || "";
        }
      }
    },
    print() {
      const previousTitle = document.title;
      document.title = " ";
      const restoreTitle = () => {
        document.title = this.originalPageTitle || previousTitle;
        window.removeEventListener("afterprint", restoreTitle);
      };
      window.addEventListener("afterprint", restoreTitle);
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.leave-encashment-slip {
  background: #eef1f5;
  min-height: 100vh;
  padding: 1.25rem;
  font-family: "Times New Roman", Times, serif;
  color: #000;
  font-size: 15px;
}

.control-bar {
  background: #fff;
  padding: 0.85rem 1.5rem;
  margin-bottom: 1.25rem;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  font-family: Arial, "Segoe UI", sans-serif;

  h4 {
    font-size: 1.2rem;
    font-weight: 700;
  }
}

.btn-modern {
  padding: 7px 16px;
  border-radius: 5px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  margin-left: 8px;
}

.btn-print {
  background: #4a5568;
  color: #fff;
}

.btn-close {
  background: #fff;
  border: 1px solid #ccc;
}

.slip-page {
  max-width: 820px;
  margin: 0 auto;
  min-height: 62vh;
}

.slip-border {
  background: #fff;
  border: 2px solid #000;
  padding: 28px 32px 24px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
  min-height: 52vh;
}

.slip-header {
  display: flex;
  align-items: flex-start;
  gap: 18px;
}

.slip-logo {
  width: 88px;
  height: 88px;
  object-fit: contain;
  flex-shrink: 0;
}

.slip-header-text {
  flex: 1;
  text-align: center;
  padding-top: 4px;
}

.school-name {
  font-size: 1.65rem;
  font-weight: 700;
  margin: 0 0 6px;
  line-height: 1.2;
  letter-spacing: 0.2px;
}

.school-address {
  margin: 0 0 8px;
  font-size: 1rem;
  color: #222;
}

.slip-title {
  font-size: 1.2rem;
  font-weight: 700;
  margin: 10px 0 0;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.slip-divider {
  border-top: 1.5px solid #000;
  margin: 18px 0;
}

.employee-info {
  display: flex;
  gap: 24px;
}

.info-col {
  flex: 1;
  min-width: 0;
}

.info-row {
  display: flex;
  align-items: baseline;
  margin-bottom: 10px;
  font-size: 1rem;
  line-height: 1.4;
}

.info-label {
  font-weight: 600;
  min-width: 118px;
  flex-shrink: 0;
}

.info-sep {
  margin-right: 8px;
  font-weight: 600;
}

.info-value {
  flex: 1;
  word-break: break-word;
}

.info-name {
  font-weight: 700;
  text-transform: uppercase;
}

.details-grid {
  display: flex;
  gap: 0;
  border: 1.5px solid #000;
  min-height: 160px;
}

.details-panel {
  flex: 1;
  display: flex;
  flex-direction: column;

  & + .details-panel {
    border-left: 1.5px solid #000;
  }
}

.panel-head {
  text-align: center;
  font-weight: 700;
  font-size: 1.05rem;
  padding: 10px 12px;
  border-bottom: 1.5px solid #000;
  background: #f5f5f5;
}

.panel-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 16px 20px;
}

.balance-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  font-size: 1.05rem;
  border-bottom: 1px solid #ccc;

  &:last-child {
    border-bottom: none;
  }
}

.balance-label {
  font-weight: 600;
}

.balance-value {
  font-weight: 700;
  font-size: 1.15rem;
  min-width: 60px;
  text-align: right;
}

.amount-value {
  font-size: 1.25rem;
  font-weight: 700;
}

.amount-only-row {
  border-bottom: none !important;
  padding: 18px 0;
}

.notes-section {
  margin-top: 20px;
}

.notes-label {
  font-weight: 700;
  margin-bottom: 6px;
  font-size: 1rem;
}

.notes-box {
  border: 1.5px solid #000;
  min-height: 56px;
  padding: 10px 14px;
  font-size: 1rem;
}

.signature-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 24px 12px 12px;
}

.signature-block {
  flex: 1;
  text-align: center;
  font-size: 1rem;
  font-weight: 600;

  .signature-line {
    border-top: 1.5px solid #000;
    margin: 52px 12px 8px;
  }
}

@media print {
  .no-print {
    display: none !important;
  }

  .leave-encashment-slip {
    background: #fff !important;
    padding: 0 !important;
    min-height: auto !important;
    font-size: 13pt !important;
  }

  .slip-page {
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 1.4cm 1.2cm 0.8cm !important;
    min-height: 58vh !important;
    box-sizing: border-box;
  }

  .slip-border {
    box-shadow: none !important;
    border: 2.5px solid #000 !important;
    padding: 1.1cm 1.2cm 0.9cm !important;
    min-height: 14.5cm !important;
    page-break-inside: avoid;
  }

  .slip-logo {
    width: 2.4cm !important;
    height: 2.4cm !important;
  }

  .school-name {
    font-size: 22pt !important;
  }

  .school-address {
    font-size: 12pt !important;
  }

  .slip-title {
    font-size: 15pt !important;
  }

  .info-row {
    font-size: 12.5pt !important;
    margin-bottom: 8pt !important;
  }

  .info-label {
    min-width: 3.2cm !important;
  }

  .slip-divider {
    margin: 14pt 0 !important;
  }

  .panel-head {
    font-size: 13pt !important;
    padding: 8pt 10pt !important;
  }

  .panel-body {
    padding: 14pt 16pt !important;
  }

  .balance-row {
    font-size: 12.5pt !important;
    padding: 10pt 0 !important;
  }

  .balance-value {
    font-size: 14pt !important;
  }

  .amount-value {
    font-size: 15pt !important;
  }

  .amount-only-row {
    padding: 14pt 0 !important;
  }

  .details-grid {
    min-height: 5.5cm !important;
  }

  .notes-box {
    min-height: 1.6cm !important;
    font-size: 12pt !important;
  }

  .signature-row {
    padding-top: 18pt !important;
  }

  .signature-block {
    font-size: 12pt !important;

    .signature-line {
      margin: 1.6cm 0.4cm 6pt !important;
    }
  }
}
</style>

<style lang="scss">
@page {
  size: A4 portrait;
  margin: 0;
}

@media print {
  html,
  body {
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>
